import { useRef, useEffect, useCallback, useState } from 'react';

/**
 * Dual-audio crossfade with true overlap.
 * Uses 2 HTMLAudioElement + 2 GainNode + 1 AudioContext.
 * On track change, the next track is preloaded on the inactive audio element,
 * then both elements play simultaneously with equal-power crossfade over `duration` seconds.
 *
 * @param {React.RefObject<HTMLAudioElement>} audioRefA — primary audio element
 * @param {React.RefObject<HTMLAudioElement>} audioRefB — secondary audio element
 * @param {{ duration?: number, samples?: number }} opts — crossfade params
 * @returns {{ activeIndex: 0|1, startCrossfadeTo: (nextTrackUrl: string) => Promise<void>, isIOS: boolean }}
 */
export function useAudioCrossfade(audioRefA, audioRefB, { duration = 3, samples = 1024 } = {}) {
  const ctxRef = useRef(null);
  const gainARef = useRef(null);
  const gainBRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0); // 0 = A is active, 1 = B is active
  const activeIndexRef = useRef(0);
  const isIOSRef = useRef(false);

  // Detect iOS (Safari) — fallback simple src-swap if true
  useEffect(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    isIOSRef.current = /iPad|iPhone|iPod/.test(ua) && !(window.MSStream);
  }, []);

  // Initialize AudioContext + 2 sources + 2 gains (lazy, on both refs present)
  useEffect(() => {
    const audioA = audioRefA?.current;
    const audioB = audioRefB?.current;
    if (!audioA || !audioB) return;
    if (ctxRef.current) return; // already initialized

    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor({ latencyHint: 'playback', sampleRate: 48000 });
      const sourceA = ctx.createMediaElementSource(audioA);
      const sourceB = ctx.createMediaElementSource(audioB);
      const gainA = ctx.createGain();
      const gainB = ctx.createGain();
      sourceA.connect(gainA);
      gainA.connect(ctx.destination);
      sourceB.connect(gainB);
      gainB.connect(ctx.destination);
      gainA.gain.value = 1; // A is active initially
      gainB.gain.value = 0;
      ctxRef.current = ctx;
      gainARef.current = gainA;
      gainBRef.current = gainB;
    } catch (e) {
      console.warn('[useAudioCrossfade] AudioContext init failed:', e);
    }
  }, [audioRefA, audioRefB]);

  // Final cleanup (real unmount only)
  useEffect(
    () => () => {
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
        gainARef.current = null;
        gainBRef.current = null;
      }
    },
    []
  );

  // Equal-power cosine crossfade curves: fadeOut = cos(t*π/2), fadeIn = sin(t*π/2)
  const buildCrossfadeCurves = useCallback(() => {
    const fadeOut = new Float32Array(samples);
    const fadeIn  = new Float32Array(samples);
    for (let i = 0; i < samples; i++) {
      const t = i / (samples - 1); // 0 → 1
      fadeOut[i] = Math.cos((t * Math.PI) / 2);
      fadeIn[i]  = Math.sin((t * Math.PI) / 2);
    }
    return { fadeOut, fadeIn };
  }, [samples]);

  /**
   * startCrossfadeTo — preload nextTrackUrl on inactive element, then crossfade.
   */
  const startCrossfadeTo = useCallback(async (nextTrackUrl) => {
    const isAActive = activeIndexRef.current === 0;
    const activeAudio   = isAActive ? audioRefA?.current : audioRefB?.current;
    const inactiveAudio = isAActive ? audioRefB?.current : audioRefA?.current;

    if (!inactiveAudio) return;

    // iOS fallback: hard-cut (no crossfade — AudioContext restrictions)
    if (isIOSRef.current || !ctxRef.current) {
      if (activeAudio) activeAudio.pause();
      inactiveAudio.src = nextTrackUrl;
      inactiveAudio.currentTime = 0;
      try { await inactiveAudio.play(); } catch { /* iOS autoplay restriction */ }
      const nextIndex = isAActive ? 1 : 0;
      activeIndexRef.current = nextIndex;
      setActiveIndex(nextIndex);
      return;
    }

    const ctx = ctxRef.current;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { /* iOS sometimes refuses */ }
    }

    const fadeOutGain = isAActive ? gainARef.current : gainBRef.current;
    const fadeInGain  = isAActive ? gainBRef.current : gainARef.current;

    // Preload next track on inactive element
    inactiveAudio.src = nextTrackUrl;
    inactiveAudio.currentTime = 0;
    try {
      await inactiveAudio.play();
    } catch (e) {
      console.warn('[useAudioCrossfade] inactive audio play failed:', e);
      return;
    }

    // Apply equal-power crossfade curves over `duration` seconds
    const { fadeOut, fadeIn } = buildCrossfadeCurves();
    const t0 = ctx.currentTime;
    fadeOutGain.gain.setValueCurveAtTime(fadeOut, t0, duration);
    fadeInGain.gain.setValueCurveAtTime(fadeIn, t0, duration);

    // Switch active ref after crossfade completes
    const nextIndex = isAActive ? 1 : 0;
    activeIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);
  }, [audioRefA, audioRefB, duration, buildCrossfadeCurves]);

  return {
    activeIndex,
    startCrossfadeTo,
    isIOS: isIOSRef.current,
  };
}
